import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import '../../styles/riskCenter.scss';

import BreadcrumNested from '../../Components/BreadcrumNested';

import { RootState, useAppSelector } from '../../redux/store';
import {
  deleteOrCancelShift,
  getShiftPreviewDataStart,
} from '../../redux/orderSlice';
import ShiftDateHeader from './components/ShiftDateHeader';
import CustomButton from '../../Components/CustomButton';
import ShiftPreviewShimmer from './components/ShiftPreviewShimmer';
import ShiftDetails from './components/ShiftDetails';

import {
  getEndDayTimestamp,
  getStartDayTimestamp,
} from '../../Utils/helperFns';
import { useLocation, useNavigate } from 'react-router-dom';
import WarningDialog from '../../Components/WarningDialog';
import moment from 'moment';

export default function PreviewShift() {
  const [pageNo] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [checkbox, setCheckbox] = useState<any[]>([]);
  const [isRangeSelected, setIsRangeSelected] = useState(false);
  const navigate = useNavigate();

  const { shiftPreviewData, loader } = useAppSelector(
    (state: RootState) => state.order,
  );

  const { previewShiftDetails } = useAppSelector(
    (state: RootState) => state.common,
  );
  const [warning, setWarning] = useState(false);
  const { state } = useLocation();
  useEffect(() => {
    if (previewShiftDetails && previewShiftDetails?.cart) {
      const cartId =
        previewShiftDetails?.cart[0]?.groupedProducts[0]?.products[0]?.cartId;
      const fromDate = getStartDayTimestamp(
        previewShiftDetails?.shiftTimes?.fromDate,
      );
      const toDate = getEndDayTimestamp(
        previewShiftDetails?.shiftTimes?.toDate,
      );

      const query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;

      getShiftPreviewDataStart(query);
    }
  }, []);

  const handleDateClick = (date: any) => {
    const seletedFromDate = getStartDayTimestamp(date);
    const selectedToDate = getEndDayTimestamp(date);

    const cartId =
      previewShiftDetails?.cart[0]?.groupedProducts[0]?.products[0]?.cartId;
    let query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${selectedToDate}&fromDate=${seletedFromDate}`;
    if (selectedDate) {
      if (
        dayjs(date).isSame(dayjs(selectedDate?.start), 'day') ||
        dayjs(date).isSame(dayjs(selectedDate?.end), 'day') ||
        date.isBetween(selectedDate?.start, selectedDate?.end, 'day')
      ) {
        const fromDate = getStartDayTimestamp(
          previewShiftDetails?.shiftTimes?.fromDate,
        );
        const toDate = getEndDayTimestamp(
          previewShiftDetails?.shiftTimes?.toDate,
        );
        query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;
        setSelectedDate(null);
        setIsRangeSelected(false);
      } else {
        // Calculate current selectedDate range end timestamp
        const selectedDateEndTimestamp = getEndDayTimestamp(selectedDate?.end);

        if (
          dayjs(date).isAfter(dayjs(selectedDateEndTimestamp)) &&
          !isRangeSelected
        ) {
          // New date is after the previous selected range end
          const fromDate = getStartDayTimestamp(selectedDate?.start);
          const toDate = getEndDayTimestamp(date);
          query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${toDate}&fromDate=${fromDate}`;
          // Optionally, you may store the range here instead of single date:
          setSelectedDate({
            start: selectedDate.start,
            end: date,
          });
          setIsRangeSelected(true);
        } else {
          // Otherwise treat as single date selection
          query = `limit=10&cartIds=${cartId}&pageNo=${pageNo}&toDate=${selectedToDate}&fromDate=${seletedFromDate}`;
          setSelectedDate({
            start: date,
            end: date,
          });
          setIsRangeSelected(false);
        }
      }
    } else {
      setSelectedDate({
        start: date,
        end: date,
      });
    }
    getShiftPreviewDataStart(query);
  };

  // Track when data is loaded for the first time
  useEffect(() => {
    if (shiftPreviewData && isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [shiftPreviewData, isInitialLoading]);

  const totalShifts = useMemo(() => {
    return checkbox.reduce(
      (total: number, item: any) => total + (item?.checkedIds?.length ?? 0),
      0,
    );
  }, [checkbox]);

  if (loader) {
    return <ShiftPreviewShimmer />;
  }

  return (
    <div className="eventDetail pageBody">
      <div className="title-flex mb-24">
        <div>
          <div className="d-flex align-center mb-12">
            <button
              className="blank-btn pl0 mr2"
              onClick={() => navigate(state.url, { state: state })}
            >
              <i className="back-icon"></i>
            </button>
            <h1 className=" MuiTypography-h3 fw600 ">Preview Shifts</h1>
          </div>
          <BreadcrumNested
            breadcrums={
              state.from == 'store'
                ? [
                  {
                    name: 'Store',
                    url: '/store',
                  },
                  {
                    name: 'Start an order',
                    url: state.url,
                  },
                  {
                    name: isEditMode ? 'Edit Shift' : 'Preview Shift',
                    url: '',
                  },
                ]
                : [
                  {
                    name: 'Store',
                    url: '/store',
                  },
                  {
                    name: 'Start an order',
                    url: state.url,
                  },

                  { name: 'Preview Shift', url: '' },
                ]
            }
          />
        </div>

        <div>
          {!isEditMode && (
            <CustomButton
              text="Edit Shift"
              showIcon={false}
              id="add-shift"
              variant="contained"
              size="large"
              color="primary"
              className="min_width_btn_160"
              onClick={() => {
                console.log('Edit Shift');
                setIsEditMode(true);
              }}
            />
          )}
        </div>
      </div>

      <div className="">
        <>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ShiftDateHeader
                cartData={shiftPreviewData?.cartDetails || []}
                handleDateClick={handleDateClick}
                selectedDate={selectedDate}
              />

              <div style={{ marginTop: 24 }}>
                <ShiftDetails
                  cartData={shiftPreviewData?.data?.data || []}
                  cartDetails={shiftPreviewData?.cartDetails || []}
                  totalWorksheets={shiftPreviewData?.data?.total}
                  isEditMode={isEditMode}
                  setIsEditMode={setIsEditMode}
                  setCheckbox={setCheckbox}
                  setSelectedDate={setSelectedDate}
                  onDeleteShiftsClick={() => setWarning(true)}
                  totalShifts={totalShifts}
                />
              </div>
            </Grid>
          </Grid>
        </>
      </div>
      <WarningDialog
        open={warning}
        handleClose={() => setWarning(false)}
        title={`Delete ${totalShifts > 1 ? 'Shifts' : 'Shift'}?`}
        description={`Are you sure you want to delete ${totalShifts} ${totalShifts > 1 ? 'shifts' : 'shift'
          }?`}
        onClick={() => {
          deleteOrCancelShift(checkbox, 'cart', () => {
            setWarning(false);
            let remaining = 0;
            let earliestStartDate: string | null =
              previewShiftDetails?.shiftTimes?.fromDate;
            let latestEndDate: string | null =
              previewShiftDetails?.shiftTimes?.toDate;

            // Iterate shiftPreviewData filtering out deleted shifts
            shiftPreviewData?.data?.data?.forEach((group: any) => {
              group.worksheets.forEach((worksheet: any) => {
                // Filter out shifts included in checkbox by matching shift _id or cartId
                const remainingShifts = worksheet.shifts.filter(
                  (shift: any) =>
                    !checkbox.some(c => c.checkedIds.includes(shift._id)),
                );
                remaining += remainingShifts.length;
                remainingShifts.forEach((shift: any) => {
                  if (shift.actualStartTime) {
                    if (
                      !earliestStartDate ||
                      moment(shift.actualStartTime).isBefore(
                        moment(earliestStartDate),
                      )
                    ) {
                      earliestStartDate = shift.actualStartTime;
                    }
                  }

                  if (shift.actualEndTime) {
                    if (
                      !latestEndDate ||
                      moment(shift.actualEndTime).isAfter(moment(latestEndDate))
                    ) {
                      latestEndDate = shift.actualEndTime;
                    }
                  }
                });
              });
            });

            // Fallback if no dates found
            if (remaining == 0) {
              navigate(state.url, {
                state,
                replace: true,
              });
              return;
            }
            const fromDate = getStartDayTimestamp(earliestStartDate);
            const toDate = getEndDayTimestamp(latestEndDate);
            const query = `limit=10&cartIds=${previewShiftDetails?.cart[0]?.groupedProducts[0]?.products[0]
                ?.cartId
              }&pageNo=${1}&toDate=${toDate}&fromDate=${fromDate}`;
            getShiftPreviewDataStart(query);
            setCheckbox([]);
          });
        }}
        cancelBtnText="Cancel"
        confirmBtnText="Yes, Continue"
        type="warning"
      />
    </div>
  );
}
